import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// ── Pages ──────────────────────────────────────────────────
import HomePage       from "./components/screens/HomePage";
import LoginPage      from "./components/screens/LoginPage";
import Register       from "./components/screens/RegisterPage";
import ShopPage       from "./components/screens/Shop";
import CartPage       from "./components/screens/CartPage";
import CheckoutPage   from "./components/screens/CheckOutPage";
import AdminPage      from "./components/screens/AdminPage";
import ContactForm    from "./components/screens/ContactForm";   // ✅ Added
import ReviewForm     from "./components/screens/ReviewForm";     // ✅ Added

// ── Route guards ───────────────────────────────────────────
import ProtectedRoute from "./components/screens/ProtectedRoute";
import AdminRoute     from "./components/screens/AdminRoute";

// ── Layout ─────────────────────────────────────────────────
import Navbar  from "./components/screens/Navbar";
import Footer  from "./components/screens/Footer";

// ── Auth ───────────────────────────────────────────────────
import { checkUserSession } from "./components/firebase/auth";

import "./App.css";

/* ─── Minimal placeholder pages for legal routes ─────────── */
function PrivacyPolicy() {
  return (
    <div style={{
      maxWidth: 760,
      margin: "0 auto",
      padding: "clamp(2rem,6vw,4rem) clamp(1rem,4vw,2rem)",
      fontFamily: "'Outfit', system-ui, sans-serif",
      color: "#3d3530",
      lineHeight: 1.7,
    }}>
      <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "#c0506e", margin: "0 0 .4rem" }}>
        GlossyLuxHair
      </p>
      <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(2rem,5vw,2.75rem)", fontWeight: 700, color: "#1a1510", margin: "0 0 2rem" }}>
        Privacy Policy
      </h1>
      {[
        ["Information We Collect", "We collect information you provide when creating an account, placing an order, or contacting us — including your name, email address, phone number and delivery address."],
        ["How We Use Your Information", "Your information is used solely to process orders, send order updates, and respond to your enquiries. We do not sell or share your personal data with third parties."],
        ["Data Storage", "Your data is stored securely using Firebase (Google Cloud). We retain order data for up to 2 years for record-keeping purposes."],
        ["Cookies", "We use essential cookies only to keep you logged in and maintain your shopping cart. We do not use advertising or tracking cookies."],
        ["Your Rights", "You may request deletion of your account and associated data at any time by contacting us at glossyluxhair@gmail.com."],
        ["Contact", "For any privacy-related queries, reach us at: glossyluxhair@gmail.com or call 0530 557 187."],
      ].map(([title, body]) => (
        <section key={title} style={{ marginBottom: "1.75rem" }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.35rem", fontWeight: 700, color: "#8f2d46", margin: "0 0 .5rem" }}>{title}</h2>
          <p style={{ margin: 0, fontSize: 15 }}>{body}</p>
        </section>
      ))}
      <p style={{ fontSize: 12, color: "#9e9189", marginTop: "3rem", borderTop: "1px solid #e8e3dc", paddingTop: "1rem" }}>
        Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
      </p>
    </div>
  );
}

function TermsOfService() {
  return (
    <div style={{
      maxWidth: 760,
      margin: "0 auto",
      padding: "clamp(2rem,6vw,4rem) clamp(1rem,4vw,2rem)",
      fontFamily: "'Outfit', system-ui, sans-serif",
      color: "#3d3530",
      lineHeight: 1.7,
    }}>
      <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "#c0506e", margin: "0 0 .4rem" }}>
        GlossyLuxHair
      </p>
      <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(2rem,5vw,2.75rem)", fontWeight: 700, color: "#1a1510", margin: "0 0 2rem" }}>
        Terms of Service
      </h1>
      {[
        ["Acceptance", "By using glossyluxhair.com you agree to these terms. If you do not agree, please do not use the site."],
        ["Products & Pricing", "All prices are displayed in Ghana Cedis (GH₵). We reserve the right to update pricing at any time. Product images are representative and actual items may vary slightly."],
        ["Orders & Payment", "Orders are confirmed once payment is successfully processed via Paystack. We reserve the right to cancel orders due to stock availability or pricing errors."],
        ["Shipping & Delivery", "We deliver across Ghana. Delivery times are estimates and may vary. Risk of loss transfers to you upon handover to the delivery service."],
        ["Returns & Refunds", "We offer a 30-day return policy for unused items in original condition. Contact us within 30 days of receipt to initiate a return."],
        ["Intellectual Property", "All content on this site — including images, logos and text — is owned by GlossyLuxHair and may not be reproduced without permission."],
        ["Contact", "Questions about these terms? Contact us at glossyluxhair@gmail.com or call 0530 557 187."],
      ].map(([title, body]) => (
        <section key={title} style={{ marginBottom: "1.75rem" }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.35rem", fontWeight: 700, color: "#8f2d46", margin: "0 0 .5rem" }}>{title}</h2>
          <p style={{ margin: 0, fontSize: 15 }}>{body}</p>
        </section>
      ))}
      <p style={{ fontSize: 12, color: "#9e9189", marginTop: "3rem", borderTop: "1px solid #e8e3dc", paddingTop: "1rem" }}>
        Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
      </p>
    </div>
  );
}

/* ─── 404 page ────────────────────────────────────────────── */
function NotFound() {
  return (
    <div style={{
      minHeight: "60vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Outfit', system-ui, sans-serif",
      textAlign: "center",
      padding: "2rem",
      gap: "1rem",
    }}>
      <p style={{ fontSize: 64, margin: 0 }}>🌸</p>
      <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(2rem,6vw,3.5rem)", fontWeight: 700, color: "#1a1510", margin: 0 }}>
        Page Not Found
      </h1>
      <p style={{ fontSize: 15, color: "#9e9189", margin: 0 }}>
        The page you're looking for doesn't exist.
      </p>
      <a href="/" style={{
        marginTop: "0.5rem",
        padding: "0.85rem 2rem",
        fontFamily: "'Outfit', system-ui, sans-serif",
        fontWeight: 600,
        fontSize: 14,
        color: "#fff",
        background: "#c0506e",
        borderRadius: 12,
        textDecoration: "none",
        boxShadow: "0 4px 16px rgba(192,80,110,.25)",
      }}>← Back to Home</a>
    </div>
  );
}

/* ─── Animated routes ─────────────────────────────────────── */
function AnimatedRoutes() {
  const location = useLocation();
  return (
    <main className="app-main" id="main-content" key={location.pathname}>
      <Routes location={location}>
        {/* ── Public routes ── */}
        <Route path="/"               element={<HomePage />} />
        <Route path="/login"          element={<LoginPage />} />
        <Route path="/register"       element={<Register />} />
        <Route path="/contact"        element={<ContactForm />} />  {/* ✅ Added */}
        <Route path="/reviews"        element={<ReviewForm />} />    {/* ✅ Added */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} /> {/* ✅ Added */}
        <Route path="/terms-of-service" element={<TermsOfService />} /> {/* ✅ Added */}

        {/* ── Protected routes (require login) ── */}
        <Route path="/shop" element={
          <ProtectedRoute><ShopPage /></ProtectedRoute>
        } />
        <Route path="/cart" element={
          <ProtectedRoute><CartPage /></ProtectedRoute>
        } />
        <Route path="/checkout" element={
          <ProtectedRoute><CheckoutPage /></ProtectedRoute>
        } />

        {/* ── Admin route ── */}
        <Route path="/admin" element={
          <AdminRoute><AdminPage /></AdminRoute>
        } />

        {/* ✅ 404 catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
  );
}

/* ─── Loading screen ──────────────────────────────────────── */
function LoadingScreen() {
  return (
    <div className="loading-screen" role="status" aria-live="polite">
      <div className="loading-inner">
        <div className="loading-logo" aria-hidden="true">
          <span className="loading-logo-icon" />
        </div>
        <div className="loading-bar-track">
          <div className="loading-bar-fill" />
        </div>
        <p className="loading-label">Getting things ready…</p>
      </div>
    </div>
  );
}

/* ─── Error toast ─────────────────────────────────────────── */
function ErrorToast({ message, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="error-toast" role="alert">
      <span className="error-toast-icon" aria-hidden="true">!</span>
      <p className="error-toast-msg">{message}</p>
      <button
        className="error-toast-close"
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >×</button>
    </div>
  );
}

/* ─── Root App ────────────────────────────────────────────── */
function App() {
  const dispatch = useDispatch();
  const { user, loading, error } = useSelector((state) => state.auth);
  const [toastVisible, setToastVisible] = useState(false);

  // ✅ Fixed: only run once on mount, not on every user change
  useEffect(() => {
    dispatch(checkUserSession());
  }, [dispatch]);

  useEffect(() => {
    if (error) setToastVisible(true);
  }, [error]);

  if (loading) return <LoadingScreen />;

  return (
    <Router>
      <div className="app-root">
        {/* Skip to main content — accessibility */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>

        <Navbar />

        {/* id="main-content" moved into AnimatedRoutes → <main> */}
        <div className="app-content">
          <AnimatedRoutes />
        </div>

        <Footer />

        {toastVisible && error && (
          <ErrorToast
            message={error}
            onDismiss={() => setToastVisible(false)}
          />
        )}
      </div>
    </Router>
  );
}

export default App;